import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, memo, useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { INK, NEUTRAL } from "../../theme/tokens";
import { NotificationType, type Recommendation, type ShiftRequest } from "../../utils/models";
import { isRequestInstanceOfReplaceShiftRequest } from "../../utils/utilFunctions";
import { CheckMark } from "../UIToolkit/Animations/Checkmark";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { RecommendationsColor } from "./RecommendationColor";
import { RecommendationsSubTitle, RecommendationsTitle } from "./Utils/RecommendationsUtils";

type RecommendationItemProps = {
	recommendation: Recommendation;
	onApprove: (recommendation: Recommendation) => void;
	showSuccessAnimation?: boolean;
};

export const RecommendationItem: FC<RecommendationItemProps> = memo(({ recommendation, onApprove, showSuccessAnimation }) => {
	const { getNurseFormattedName } = useHospitalData();
	const { staffRequests } = useRequestsContext();
	const relevantRequest = useMemo<ShiftRequest | null | undefined>(() => {
		/**
		 * Data may be null or undefined if the recommendation is not linked to a specific request
		 */
		return typeof recommendation?.extra_data?.data === "string" ? staffRequests.find((req) => req.id === recommendation.extra_data.data) : null;
	}, [recommendation, staffRequests]);

	const title = useMemo<string>(() => {
		if (relevantRequest && relevantRequest.request_type === NotificationType.ReplaceShift && isRequestInstanceOfReplaceShiftRequest(relevantRequest)) {
			return `${getNurseFormattedName(relevantRequest.replacement_nurse_id)} Replace Request`;
		}
		return RecommendationsTitle[recommendation.type];
	}, [relevantRequest, getNurseFormattedName, recommendation.type]);

	const shiftTimes = useMemo<string>(() => {
		return relevantRequest ? `${relevantRequest.message.shift_start.toFormat("h:mm a")} - ${relevantRequest.message.shift_end.toFormat("h:mm a")}` : "";
	}, [relevantRequest]);

	return (
		<Box
			sx={{
				borderRadius: "8px",
				my: 2,
				p: 1.5,
				color: INK.SECONDARY,
				border: `1px solid ${NEUTRAL[200]}`,
				position: "relative",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<RecommendationsColor severity={recommendation.severity} />
				<Typography sx={{ fontWeight: 600 }}>{title}</Typography>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					mt: 1,
					color: alpha(INK.SECONDARY, 0.5),
					fontSize: "14px",
					fontWeight: 600,
					pb: 1,
					justifyContent: "space-between",
					whiteSpace: "nowrap",
					overflow: "hidden",
				}}
			>
				<Box>
					<Typography>{recommendation.date.toFormat("ccc, MMM d")}</Typography>
					<Typography>{shiftTimes}</Typography>
				</Box>
				<SystemButton sx={{ fontSize: "14px", height: "32px" }} onClick={(_) => onApprove(recommendation)}>
					{RecommendationsSubTitle[recommendation.type]}
				</SystemButton>
			</Box>
			{showSuccessAnimation && (
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 9999,
						pointerEvents: "none",
					}}
				>
					<CheckMark />
				</Box>
			)}
		</Box>
	);
});
